import { useMutation } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  ContributionFeeQuoteRequestDto,
  ContributionFeeQuoteResponseDto,
} from "@/features/fundraising/api/dto";

/**
 * Corps Postman `FeeQuoteRequestDto` : tous les champs du contrat, y
 * compris `transactionReference` (requis à la désérialisation). UUID
 * généré si l'appelant n'en fournit pas — le devis précède l'initiate,
 * donc pas encore de `FPT-…` wallet.
 */
const toFeeQuoteBody = (
  request: ContributionFeeQuoteRequestDto
): ContributionFeeQuoteRequestDto => ({
  productCode: request.productCode,
  providerId: request.providerId ?? null,
  customerSegment: request.customerSegment,
  feePurpose: request.feePurpose,
  amountMinor: request.amountMinor,
  currency: request.currency,
  transactionReference:
    request.transactionReference.trim() || crypto.randomUUID(),
  beneficiaryPartyId: request.beneficiaryPartyId ?? null,
  promoCode: request.promoCode ?? null,
  partyId: request.partyId ?? null,
});

/**
 * Devis de frais d'une contribution (`POST /fee-quotes`, fee-engine §6.5) —
 * alimente la section frais de la ShareablePreview avant confirmation
 * (règle projet, cahier §9.5).
 *
 * Idempotency-Key OBLIGATOIRE (§4.7) : le scope garde la même clé au retry
 * automatique d'une tentative métier ; un 422 (même clé, corps différent =
 * saisie corrigée) régénère la clé.
 *
 * Réplique le hook `useFeeQuote` de fee-engine via le registre
 * `@/core/api/endpoints` (règle : pas d'import croisé entre features).
 */
export const useContributionFeeQuote = () =>
  useMutation({
    mutationKey: ["fundraising", "fee-quotes", "create"],
    mutationFn: (body: ContributionFeeQuoteRequestDto) =>
      mutateIdempotent<ContributionFeeQuoteResponseDto>({
        scopeId: "fundraising.fee-quotes",
        url: endpoints.feeEngine.feeQuotes.create,
        body: toFeeQuoteBody(body),
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useContributionFeeQuote failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fundraising" }
      );
    },
  });
